import React, { useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '../../supabase';
import {
  CollapsibleSection, Sparkline, StatusPill,
  SegmentedPills, RichToast,
} from '../systemKit';
import { fmtFull, fmtRelativeDate, fmtSignedPct, deltaColor, deltaPillTone } from './utils';
import AddToWatchlistModal from './AddToWatchlistModal';

// Watchlist — operator-curated tracking section. Each row shows item +
// vendor, current price, delta vs baseline (with sign-aware chip), 8-week
// sparkline, last-buy relative date, optional consecutive-increase chip,
// and a three-dot menu with Pin / Unpin / Edit notes (inline) / Remove.
//
// Pulse animation: rows where pulse_active=true get a 2s scale pulse on
// the delta chip when the section first renders. Operator gets a quiet
// nudge that something moved without us shouting.
//
// Polarity filter: section header carries [All / Up / Down] pills. Filter
// applies to non-pinned rows; pinned items always show regardless of
// filter — pin means "always visible, period."
//
// Remove flow: Remove deletes immediately and shows a 5-second RichToast
// at bottom-right with an Undo CTA. Click Undo within 5s → row is
// re-inserted with its original pinned_order + notes. After 5s the toast
// auto-dismisses and the deletion is permanent.

const UNDO_WINDOW_MS = 5000;

export default function Watchlist({
  companyId,
  onOpenDrillIn,
  onAlertCountChange,
  highlightAlerts,
  lastSeenAt,
  polarity,           // 'all' | 'up' | 'down'
  onPolarityChange,
}) {
  const [rows, setRows] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [version, setVersion] = useState(0);
  const [undoToast, setUndoToast] = useState(null);
  const undoTimerRef = useRef(null);
  const reloadRef = useRef(0);

  const reload = () => setVersion(v => v + 1);

  useEffect(() => {
    if (!companyId) return;
    let cancelled = false;
    setRows(null);
    (async () => {
      const { data } = await supabase.rpc('cost_dashboard_watchlist_state', {
        p_company_id: companyId,
      });
      if (cancelled) return;
      setRows(data || []);
    })();
    return () => { cancelled = true; };
  }, [companyId, version]);

  // Realtime — another tab editing the watchlist refreshes ours.
  useEffect(() => {
    if (!companyId) return;
    const ch = supabase
      .channel(`watchlist-${companyId}`)
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'cost_watchlist_items', filter: `company_id=eq.${companyId}` },
        () => {
          // Throttle: at most one reload per 800ms.
          const now = Date.now();
          if (now - reloadRef.current < 800) return;
          reloadRef.current = now;
          reload();
        })
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [companyId]);

  // Filtered rows. Pinned items override polarity — they always show.
  const visibleRows = useMemo(() => {
    if (!rows) return null;
    if (polarity === 'all') return rows;
    return rows.filter(r => {
      if (r.pinned_order != null) return true;  // pin overrides
      if (polarity === 'up')   return r.delta_class === 'up';
      if (polarity === 'down') return r.delta_class === 'down';
      return true;
    });
  }, [rows, polarity]);

  const alertCount = useMemo(() => {
    if (!rows) return 0;
    return rows.filter(r => r.delta_class && r.delta_class !== 'flat').length;
  }, [rows]);

  useEffect(() => {
    if (rows) onAlertCountChange?.(alertCount);
  }, [alertCount, rows, onAlertCountChange]);

  // ── Mutations ──────────────────────────────────────────────────────────────

  const removeItem = async (row) => {
    // Snapshot first so Undo restores the same pinned_order + notes.
    const snapshot = {
      company_id: companyId,
      food_item_id: row.food_item_id,
      pinned_order: row.pinned_order,
      notes: row.notes,
    };
    // Delete immediately — operator's Undo window is the safety net.
    await supabase.from('cost_watchlist_items')
      .delete()
      .eq('company_id', companyId)
      .eq('food_item_id', row.food_item_id);
    reload();

    // Toast with Undo CTA. We track the timer so Undo can cancel it.
    if (undoTimerRef.current) {
      clearTimeout(undoTimerRef.current);
      undoTimerRef.current = null;
    }
    const toastId = `watchlist-undo-${row.food_item_id}-${Date.now()}`;
    setUndoToast({
      id: toastId,
      tone: 'neutral',
      eyebrow: 'Watchlist',
      headline: `Removed ${row.item_name || 'item'}`,
      detail: 'Tap Undo within 5 seconds to restore.',
      ctaText: 'Undo',
      onCta: async () => {
        clearTimeout(undoTimerRef.current);
        undoTimerRef.current = null;
        const { data: { user } } = await supabase.auth.getUser();
        await supabase.from('cost_watchlist_items').insert({
          company_id: snapshot.company_id,
          food_item_id: snapshot.food_item_id,
          pinned_order: snapshot.pinned_order,
          notes: snapshot.notes,
          added_by: user?.id || null,
        });
        setUndoToast(null);
        reload();
      },
    });

    undoTimerRef.current = setTimeout(() => {
      setUndoToast(null);
      undoTimerRef.current = null;
    }, UNDO_WINDOW_MS);
  };

  const togglePin = async (row) => {
    const newOrder = row.pinned_order != null ? null : 0;
    await supabase.from('cost_watchlist_items')
      .update({ pinned_order: newOrder })
      .eq('company_id', companyId)
      .eq('food_item_id', row.food_item_id);
    reload();
  };

  const saveNotes = async (foodItemId, notes) => {
    await supabase.from('cost_watchlist_items')
      .update({ notes: notes || null })
      .eq('company_id', companyId)
      .eq('food_item_id', foodItemId);
    reload();
  };

  // Re-seed via the new Postgres function (extracted from the original
  // migration's inline auto-seed). Operator-triggered only — never
  // automatic after the initial seed.
  const reseed = async () => {
    await supabase.rpc('cost_dashboard_reseed_watchlist', { p_company_id: companyId });
    reload();
  };

  // ── Header content ────────────────────────────────────────────────────────

  const subhead = rows == null
    ? 'loading…'
    : rows.length === 0
      ? 'no items tracked yet'
      : `${rows.length} item${rows.length === 1 ? '' : 's'} tracked${alertCount > 0 ? ` · ${alertCount} moved` : ''}`;

  // Header action: polarity filter on the left, "+ Add item" on the right.
  // Both stop click propagation so the section doesn't toggle when used.
  const headerAction = (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 12 }}>
      {rows && rows.length > 0 && (
        <SegmentedPills
          value={polarity || 'all'}
          onChange={onPolarityChange}
          options={[
            { key: 'all',  label: 'All' },
            { key: 'up',   label: 'Up only ↑' },
            { key: 'down', label: 'Down only ↓' },
          ]}
          size="sm"
        />
      )}
      {rows && rows.length > 0 && (
        <button
          onClick={(e) => { e.stopPropagation(); setAddOpen(true); }}
          style={{
            fontSize: 12, fontWeight: 600,
            color: 'var(--accent-primary)',
            background: 'transparent', border: 'none',
            cursor: 'pointer', padding: '4px 8px',
          }}
        >
          + Add item
        </button>
      )}
    </div>
  );

  return (
    <>
      <style>{`
        @keyframes cd-pulse {
          0% { transform: scale(1); box-shadow: 0 0 0 0 color-mix(in srgb, var(--accent-primary) 35%, transparent); }
          50% { transform: scale(1.06); box-shadow: 0 0 0 6px color-mix(in srgb, var(--accent-primary) 0%, transparent); }
          100% { transform: scale(1); box-shadow: 0 0 0 0 color-mix(in srgb, var(--accent-primary) 0%, transparent); }
        }
        .cd-pulse { animation: cd-pulse 2s ease-out 1; }
        @keyframes cd-highlight-fade {
          0% { background: color-mix(in srgb, var(--accent-primary) 18%, transparent); }
          100% { background: transparent; }
        }
        .cd-highlight { animation: cd-highlight-fade 5s ease-out 1; }
      `}</style>
      <CollapsibleSection
        id="cost-dashboard-watchlist"
        title="Watchlist"
        count={rows?.length}
        countTone="neutral"
        subhead={subhead}
        headerAction={headerAction}
        defaultExpanded={true}
      >
        {!rows && <SkeletonRows />}
        {rows && rows.length === 0 && (
          <EmptyState onReseed={reseed} onAdd={() => setAddOpen(true)} />
        )}
        {rows && rows.length > 0 && visibleRows.length === 0 && (
          <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13, borderTop: '1px solid var(--border)' }}>
            No watchlist items match the {polarity === 'up' ? '"Up only"' : '"Down only"'} filter.
            {' '}<button
              onClick={() => onPolarityChange?.('all')}
              style={{ background: 'transparent', border: 'none', color: 'var(--accent-primary)', fontWeight: 600, cursor: 'pointer' }}
            >Show all</button>
          </div>
        )}
        {rows && rows.length > 0 && visibleRows.length > 0 && (
          <div role="list" style={{ borderTop: '1px solid var(--border)' }}>
            {visibleRows.map(r => (
              <WatchlistRow
                key={r.food_item_id}
                row={r}
                isAlertHighlighted={highlightAlerts && r.delta_class !== 'flat'}
                isNewSinceLastSeen={lastSeenAt && r.last_invoice_date && r.last_invoice_date > lastSeenAt.split('T')[0]}
                onOpen={() => onOpenDrillIn?.(r)}
                onTogglePin={() => togglePin(r)}
                onSaveNotes={(notes) => saveNotes(r.food_item_id, notes)}
                onRemove={() => removeItem(r)}
              />
            ))}
          </div>
        )}
      </CollapsibleSection>

      <AddToWatchlistModal
        open={addOpen}
        companyId={companyId}
        onClose={() => setAddOpen(false)}
        onAdded={reload}
        excludeFoodItemIds={(rows || []).map(r => r.food_item_id)}
      />

      {/* Undo toast — bottom-right by convention (operator-initiated action;
          this is a receipt, not a system event). 5s window. Auto-dismiss
          handled by the timer above; tapping Undo cancels and restores. */}
      {undoToast && (
        <RichToast
          toast={undoToast}
          position="bottom-right"
          onDismiss={() => {
            setUndoToast(null);
            if (undoTimerRef.current) {
              clearTimeout(undoTimerRef.current);
              undoTimerRef.current = null;
            }
          }}
        />
      )}
    </>
  );
}

// ─── Row ────────────────────────────────────────────────────────────────────

function WatchlistRow({ row, isAlertHighlighted, isNewSinceLastSeen, onOpen, onTogglePin, onSaveNotes, onRemove }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuOpenUpward, setMenuOpenUpward] = useState(false);
  const [editingNotes, setEditingNotes] = useState(false);
  const [draftNotes, setDraftNotes] = useState(row.notes || '');
  const menuBtnRef = useRef(null);

  const sparkValues = useMemo(() => {
    return (row.sparkline_points || []).map(p => Number(p.value)).filter(v => v > 0);
  }, [row.sparkline_points]);

  const tone = deltaPillTone(row.delta_pct, row.delta_class);
  const color = deltaColor(row.delta_pct, row.delta_class);
  const ci = Number(row.consecutive_increases || 0);

  // Viewport-aware menu flip: when the kebab is near the bottom of the
  // viewport, the menu opens upward instead of downward so it doesn't
  // get clipped. Decided at open-time based on the button's bounding rect.
  // Estimated menu height: 3 items × ~34px + padding = ~120px.
  const openMenu = () => {
    const ESTIMATED_MENU_HEIGHT = 120;
    if (menuBtnRef.current) {
      const rect = menuBtnRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      setMenuOpenUpward(spaceBelow < ESTIMATED_MENU_HEIGHT + 16);
    }
    setMenuOpen(true);
  };

  const closeMenu = () => setMenuOpen(false);

  const handleStartEditNotes = () => {
    setDraftNotes(row.notes || '');
    setEditingNotes(true);
    closeMenu();
  };

  const handleSaveNotes = async () => {
    await onSaveNotes(draftNotes.trim());
    setEditingNotes(false);
  };

  const handleCancelNotes = () => {
    setDraftNotes(row.notes || '');
    setEditingNotes(false);
  };

  return (
    <div
      role="listitem"
      className={isAlertHighlighted ? 'cd-highlight' : ''}
      style={{
        borderBottom: '1px solid var(--border)',
        transition: 'background 120ms ease',
      }}
    >
      <div
        onClick={onOpen}
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 2fr) 100px 130px 100px 90px 36px',
          alignItems: 'center',
          gap: 12,
          padding: '12px 16px',
          cursor: 'pointer',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-hover)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
      >
        {/* Item + vendor */}
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {row.pinned_order != null && (
              <span title="Pinned" style={{ color: 'var(--accent-primary)', fontSize: 11 }}>★</span>
            )}
            <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {row.item_name || `Item #${row.food_item_id}`}
            </span>
            {isNewSinceLastSeen && (
              <span style={{
                fontSize: 9, fontWeight: 700, letterSpacing: '0.06em',
                padding: '1px 5px', borderRadius: 4,
                background: 'var(--bg-accent-chip)', color: 'var(--accent-primary)',
                textTransform: 'uppercase',
              }}>NEW</span>
            )}
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {(row.primary_vendor || '—').replace(/\n/g, ' · ')}
            {row.notes && !editingNotes && (
              <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}> · {row.notes}</span>
            )}
          </div>
        </div>

        {/* Current price */}
        <div style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>
            {row.current_unit_price != null ? fmtFull(row.current_unit_price) : '—'}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
            {row.baseline_unit_price != null ? `base ${fmtFull(row.baseline_unit_price)}` : 'no baseline'}
          </div>
        </div>

        {/* Delta chip + consecutive */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 6, flexWrap: 'wrap' }}>
          <span className={row.pulse_active ? 'cd-pulse' : ''} style={{ display: 'inline-block', borderRadius: 6 }}>
            <StatusPill tone={tone}>
              <span style={{ color }}>{row.delta_pct != null ? fmtSignedPct(row.delta_pct) : (row.delta_class === 'pack_changed' ? 'pack' : '—')}</span>
            </StatusPill>
          </span>
          {ci >= 3 && row.delta_class === 'up' && (
            <StatusPill tone="red">{ci}× consec</StatusPill>
          )}
        </div>

        {/* Sparkline */}
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          {sparkValues.length >= 2 ? (
            <Sparkline data={sparkValues} width={80} height={24} tone="price" />
          ) : (
            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>—</span>
          )}
        </div>

        {/* Last buy */}
        <div style={{ fontSize: 12, color: 'var(--text-muted)', textAlign: 'right' }}>
          {fmtRelativeDate(row.last_invoice_date)}
        </div>

        {/* Three-dot menu */}
        <div style={{ position: 'relative' }} onClick={(e) => e.stopPropagation()}>
          <button
            ref={menuBtnRef}
            aria-label="Row actions"
            onClick={() => menuOpen ? closeMenu() : openMenu()}
            style={{
              border: 'none', background: 'transparent', cursor: 'pointer',
              padding: 4, color: 'var(--text-secondary)',
              fontSize: 16, lineHeight: 1,
              width: 24, height: 24,
              borderRadius: 4,
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-subtle)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
          >
            ⋯
          </button>
          {menuOpen && (
            <>
              <div
                onClick={closeMenu}
                style={{ position: 'fixed', inset: 0, zIndex: 40 }}
              />
              <div style={{
                position: 'absolute',
                right: 0,
                ...(menuOpenUpward
                  ? { bottom: '100%', marginBottom: 4 }
                  : { top: '100%',    marginTop: 4 }),
                background: 'var(--bg-card)',
                border: '1px solid var(--border)',
                borderRadius: 8,
                boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                zIndex: 41,
                minWidth: 180,
                padding: 4,
              }}>
                <MenuItem onClick={() => { closeMenu(); onTogglePin(); }}>
                  {row.pinned_order != null ? 'Unpin' : 'Pin to top'}
                </MenuItem>
                <MenuItem onClick={handleStartEditNotes}>
                  {row.notes ? 'Edit notes' : 'Add notes'}
                </MenuItem>
                <MenuItem onClick={() => { closeMenu(); onRemove(); }} destructive>
                  Remove from watchlist
                </MenuItem>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Inline notes editor — expands within the row when active. Replaces
          the previous window.prompt() flow with a real textarea. Save persists
          to cost_watchlist_items.notes; Cancel discards. */}
      {editingNotes && (
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            padding: '10px 16px 14px',
            background: 'var(--bg-subtle)',
            borderTop: '1px solid var(--border)',
          }}
        >
          <div style={{
            fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
            letterSpacing: '0.06em', color: 'var(--text-secondary)', marginBottom: 6,
          }}>
            Notes
          </div>
          <textarea
            value={draftNotes}
            onChange={(e) => setDraftNotes(e.target.value)}
            rows={2}
            placeholder="e.g. watching since vendor switch in March"
            autoFocus
            style={{
              width: '100%',
              padding: '8px 10px',
              fontSize: 13, lineHeight: 1.5,
              background: 'var(--bg-card)',
              border: '1px solid var(--border)',
              borderRadius: 6,
              color: 'var(--text-primary)',
              fontFamily: 'inherit',
              resize: 'vertical',
            }}
          />
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
            <button
              onClick={handleCancelNotes}
              style={{
                padding: '6px 12px', fontSize: 12, fontWeight: 500,
                background: 'transparent',
                border: '1px solid var(--border)',
                borderRadius: 6,
                color: 'var(--text-secondary)',
                cursor: 'pointer',
              }}
            >Cancel</button>
            <button
              onClick={handleSaveNotes}
              style={{
                padding: '6px 12px', fontSize: 12, fontWeight: 500,
                background: 'var(--text-primary)',
                color: 'var(--bg-card)',
                border: 'none',
                borderRadius: 6,
                cursor: 'pointer',
              }}
            >Save</button>
          </div>
        </div>
      )}
    </div>
  );
}

function MenuItem({ onClick, destructive, children }) {
  return (
    <button
      onClick={onClick}
      style={{
        width: '100%',
        textAlign: 'left',
        padding: '7px 12px',
        background: 'transparent',
        border: 'none',
        borderRadius: 6,
        cursor: 'pointer',
        fontSize: 12,
        color: destructive ? 'var(--text-critical)' : 'var(--text-primary)',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-hover)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
    >
      {children}
    </button>
  );
}

function SkeletonRows() {
  return (
    <div style={{ borderTop: '1px solid var(--border)' }}>
      {[0, 1, 2].map(i => (
        <div key={i} style={{
          display: 'grid', gridTemplateColumns: '2fr 100px 130px 100px 90px 36px',
          gap: 12, padding: '14px 16px',
          borderBottom: '1px solid var(--border)',
        }}>
          <div style={{ height: 12, background: 'var(--bg-subtle)', borderRadius: 4 }} />
          <div style={{ height: 12, background: 'var(--bg-subtle)', borderRadius: 4 }} />
          <div style={{ height: 12, background: 'var(--bg-subtle)', borderRadius: 4 }} />
          <div style={{ height: 12, background: 'var(--bg-subtle)', borderRadius: 4 }} />
          <div style={{ height: 12, background: 'var(--bg-subtle)', borderRadius: 4 }} />
          <div />
        </div>
      ))}
    </div>
  );
}

function EmptyState({ onReseed, onAdd }) {
  return (
    <div style={{ padding: 24, textAlign: 'center', borderTop: '1px solid var(--border)' }}>
      <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 12 }}>
        Your watchlist is empty. Add items you want to track here.
      </div>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
        <button
          onClick={onAdd}
          style={{
            padding: '8px 14px', fontSize: 13, fontWeight: 500,
            background: 'var(--text-primary)', color: 'var(--bg-card)',
            border: 'none', borderRadius: 8, cursor: 'pointer',
          }}
        >
          Add item
        </button>
        <button
          onClick={onReseed}
          style={{
            padding: '8px 14px', fontSize: 13, fontWeight: 500,
            background: 'var(--bg-card)', color: 'var(--text-primary)',
            border: '1px solid var(--border)', borderRadius: 8, cursor: 'pointer',
          }}
        >
          Re-add top 10 by spend
        </button>
      </div>
    </div>
  );
}

